import type { AuthAccountWithPassType } from 'api-client/accounts';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import useBriefStore from 'stores/useBriefStore';
import { truthy } from 'utils/reduce-utils';
import AccountAvatar from 'components/Utils/Account/AccountAvatar';
import { useEffect, useMemo, type ReactElement } from 'react';
import useAccountStore from 'stores/useAccountStore';
import { useBooleanFlagValue } from 'stores/useFeatureFlagStore';
import { ODSTooltip } from 'bc-ods';

type NameFormat = 'username' | 'short' | 'full';

interface PropTypes extends React.ComponentPropsWithoutRef<'span'> {
  account?: AuthAccountWithPassType;
  accountId?: number | null;
  accountUuid?: string | null;
  format?: NameFormat;
  includeJob?: boolean;
  includeAvatar?: boolean;
  includeEmail?: boolean;
  includeUserName?: boolean;
  displayAccountDetailsInline?: boolean;
  displayExternalLabel?: boolean;
  excludeParentOrganization?: boolean;
}

const messages = defineMessages({
  none: {
    id: 'AccountDisplayName.none',
    defaultMessage: 'None',
  },
  external: {
    id: 'AccountDisplayName.external',
    defaultMessage: '[External]',
  },
  userNotPartOfPlan: {
    id: 'AccountDisplayName.userNotPartOfPlan',
    defaultMessage: 'This user is not part of this plan.',
  },
});

function accountDisplayName(
  account: AuthAccountWithPassType | number | undefined,
  format: NameFormat = 'short'
): string {
  if (!account) {
    return 'None';
  }
  if (typeof account !== 'number') {
    const { rank, firstName, lastName, name: userName } = account;
    if (format === 'username') {
      return `@${userName}`;
    }

    const fullName = [firstName, lastName].filter(truthy).join(' ') || `@${userName}`;
    return format === 'short' ? fullName : [rank, fullName].filter(truthy).join(' ');
  }
  return `#${account}`;
}

function getJobDetailsByAccount(
  account: AuthAccountWithPassType | number,
  excludeParentOrg?: boolean
) {
  if (typeof account !== 'number') {
    const { jobTitle, parentOrganization } = account;
    const displayJob = [jobTitle, !excludeParentOrg ? parentOrganization : null]
      .filter(truthy)
      .join(', ');
    return displayJob;
  }
  return '';
}

const accountDisplayJob = (
  account: AuthAccountWithPassType | number,
  excludeParentOrg = false
): ReactElement | string => {
  const displayJob = getJobDetailsByAccount(account, excludeParentOrg);
  return (
    displayJob && (
      <span className="text-secondary" css={(theme) => ({ fontSize: theme.text.size.smaller })}>
        {displayJob}
      </span>
    )
  );
};

function AccountDisplay({
  account,
  accountId,
  accountUuid,
  format,
  includeJob,
  includeAvatar,
  includeEmail,
  includeUserName,
  displayAccountDetailsInline,
  excludeParentOrganization,
  displayExternalLabel = false,
  ...spanProps
}: PropTypes): ReactElement {
  const accountIdToUse = accountId ?? account?.id;
  const accountUuidToUse = accountUuid ?? account?.uuid;

  const loadAccount = useAccountStore((state) => state.loadAccount);
  // NOTE(jeff): this selector kind of smells
  const briefAccount = useBriefStore(
    ({ brief }) => {
      // When PINT the briefStore is not loaded so we retrieve from localStorage
      if (!Object.keys(brief).length) {
        const authors = localStorage.getItem('authors');
        if (authors) {
          brief = JSON.parse(authors);
        }
      }
      if (!accountIdToUse && !accountUuidToUse) {
        return undefined;
      }
      // TODO[PIBE-246]: collapse to single identifier once id columns are removed
      const matchesAccount = (id?: number | string, uuid?: string | null) =>
        (accountIdToUse && accountIdToUse === id) ||
        (accountUuidToUse && accountUuidToUse === uuid);

      const owner = brief?.briefOwner?.account;
      if (owner && matchesAccount(owner.id, owner.uuid)) {
        return owner;
      }

      const storeBriefAccount =
        (accountUuidToUse && brief.briefAccess?.account?.[accountUuidToUse]?.account) ||
        (accountIdToUse && brief.briefAccess?.account?.[accountIdToUse]?.account);
      if (storeBriefAccount) {
        return storeBriefAccount;
      }
      const groups = Object.values(brief.briefAccess?.group ?? {});
      const userGroup = groups.find((group) =>
        group?.group?.users?.some((user) => matchesAccount(user.id, user.uuid))
      );
      if (userGroup) {
        const user = userGroup.group?.users?.find((briefUser) =>
          matchesAccount(briefUser.id, briefUser.uuid)
        );
        if (user) {
          return {
            id: user.id,
            avatarColor: user.avatarColor,
            avatarUrl: user.avatarUrl,
            firstName: user.firstName,
            lastName: user.lastName,
            name: user.name,
          };
        }
      }
      return undefined;
    },
    (a: AuthAccountWithPassType | undefined, b: AuthAccountWithPassType | undefined) =>
      a?.id === b?.id
  );

  // TODO[PIBE-400]: drop flag check when all callers enforce the account identity contract
  const useUuid = useBooleanFlagValue('USE_ACCOUNT_UUID');
  const accountLookupKey = useUuid ? accountUuidToUse : accountIdToUse;
  const externalUser = useAccountStore((state) =>
    accountLookupKey ? state.accounts[accountLookupKey] : undefined
  );

  useEffect(() => {
    if (!briefAccount && !externalUser && accountLookupKey) {
      loadAccount(accountLookupKey);
    }
  }, [briefAccount, accountLookupKey, loadAccount, externalUser]);

  const accountToUse = account ?? briefAccount ?? externalUser;

  const isExternalUser = useMemo(
    () => !!externalUser && externalUser === accountToUse,
    [accountToUse, externalUser]
  );

  const name = accountDisplayName(accountToUse, format);
  const intl = useIntl();

  return accountToUse ? (
    <span {...spanProps}>
      {includeAvatar && (
        <AccountAvatar
          account={accountToUse}
          css={{ width: '1.25rem', height: '1.25rem' }}
          className="me-1 flex-shrink-0"
          isApi={accountToUse?.accountType?.startsWith('api')}
        />
      )}
      {displayAccountDetailsInline ? (
        <div className="small">
          <span data-testid="account-details" className="fw-medium">
            {name}
          </span>
          {includeJob && <> {getJobDetailsByAccount(accountToUse, excludeParentOrganization)}</>}
          {includeUserName && ` - ${accountToUse.name}`}
          {includeEmail && accountToUse.email && ` - ${accountToUse.email}`}
        </div>
      ) : (
        <>
          <span
            data-testid="account-details"
            className="d-inline-flex flex-column"
            css={{ lineHeight: 1, overflowWrap: 'anywhere' }}
          >
            {name}
            {includeJob && <> {accountDisplayJob(accountToUse, excludeParentOrganization)}</>}
            {includeUserName && ` - ${accountToUse.name}`}
            {includeEmail && accountToUse.email && ` - ${accountToUse.email}`}
          </span>
          {isExternalUser && displayExternalLabel && (
            <ODSTooltip
              ignoreOverflow
              message={<FormattedMessage {...messages.userNotPartOfPlan} />}
            >
              <em className="ms-1" css={{ cursor: 'help' }}>
                <FormattedMessage {...messages.external} />
              </em>
            </ODSTooltip>
          )}
        </>
      )}
    </span>
  ) : (
    <span {...spanProps}>{intl.formatMessage(messages.none)}</span>
  );
}

export default AccountDisplay;
export { accountDisplayName, accountDisplayJob };
